import getEnvConfig from "./config";

const MINIMUM_SUPPORTED_SAFARI_VERSION = 16;

/**
 * We support Chrome, Edge and Firefox.
 *
 * Browser detection is messy and fragile, so we keep things simple and make only a limited effort to detect the user's
 * browser.
 *
 * Browsers that include "Chrome" or "Firefox" in the User-Agent will be classified as supported. Edge satisfies this
 * condition, as it sends "Chrome" in the UA. Notably, Internet Explorer and Safari will be classified as not supported.
 *
 * Support for mobile browsers must be enabled in the game configuration (cdk/environment.py).
 * Checking the presence of the "Mobile" string is enough to detect most of the
 * mobile browsers, including the 2 most used ones (Dec-2021: Chrome, Safari).
 */
export const isSupportedBrowser = () => {

    const isChrome = isChromeBrowser();
    const isFirefox = isFirefoxBrowser();
    const isSafari = isSupportedSafariBrowser();
    const isMobile = isMobileBrowser();

    const env = getEnvConfig();

    return (isChrome || isFirefox || (isSafari && env.has_safari_support)) &&
      (env.has_mobile_support || !isMobile) &&
      checkBrowserArch();
}

export const isMobileBrowser = (): boolean => {
    return navigator.userAgent.includes('Mobile');
}

export const isSafariBrowser = (): boolean => {
    return navigator.userAgent.includes('AppleWebKit');
}

export const isSupportedSafariBrowser = (): boolean => {
    if (isSafariBrowser()) {
        const versionMatch = navigator.userAgent.match(/Version\/(\S+)/);
        if (versionMatch) {
            const version = parseFloat(versionMatch[1]);
            return version >= MINIMUM_SUPPORTED_SAFARI_VERSION;
        }
    }
    return false;
}

export const isChromeBrowser = (): boolean => {
    return navigator.userAgent.includes('Chrome');
}

export const isFirefoxBrowser = (): boolean => {
    return navigator.userAgent.includes('Firefox');
}

export const haveWebGl2Support = () => {
    try {
        const canvas = document.createElement('canvas');
        return !!canvas.getContext('webgl2');
    } catch (e) {
        return false;
    }
}

/**
 * Check if the user's browser is 64-bit.
 *
 * The check is limited to Windows and Linux (non-Android) devices. For other devices, like Mac,
 * we assume the browser is 64-bit.
 * @returns {boolean} True if the user's browser is 64-bit, false otherwise.
 */
export const checkBrowserArch = (): boolean => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Windows')) {
        return userAgent.includes('WOW64') || userAgent.includes('Win64');
    } else if (userAgent.includes('Linux') && !userAgent.includes('Android')) {
        return userAgent.includes('x86_64');
    }
    return true;
}

export const getRecommendBrowserMessage = (gameName: string): string => {
    const env = getEnvConfig();

    if (env.has_mobile_support && isMobileBrowser()) {
        if (env.has_safari_support) {
            return `For the best experience, we recommend running ${gameName} on Google Chrome or Safari (version ${MINIMUM_SUPPORTED_SAFARI_VERSION} or above).`;
        } else {
            return `For the best experience, we recommend running ${gameName} on Google Chrome.`;
        }
    }

    return `For the best experience, we recommend running ${gameName} on the 64-bit desktop version of Google Chrome, Microsoft Edge or Mozilla Firefox.`;
}


export const getBrowserErrorMessage = (): string => {
    const env = getEnvConfig();

    if (env.has_mobile_support && isMobileBrowser()) {
        if (env.has_safari_support) {
            return `If you aren't already doing so, please try running the game on Google Chrome or Safari (version ${MINIMUM_SUPPORTED_SAFARI_VERSION}or above).`;
        } else {
            return `If you aren't already doing so, please try running the game on Google Chrome.`;
        }
    }

    return "If you aren't already doing so, please try running the game on the 64-bit desktop version of Google Chrome, Microsoft Edge or Mozilla Firefox.";
}

/**
 * True on an iOS device (iPhone / iPad / iPod). iPadOS 13+ reports a Mac User-Agent, so we also treat
 * a "Macintosh" UA that exposes multi-touch as an iPad — otherwise an iPad would miss the App Store.
 */
export const isIosDevice = (): boolean => {
    const userAgent = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return true;
    }
    return userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1;
}

export const isAndroidDevice = (): boolean => {
    return /Android/i.test(navigator.userAgent);
}